import { forwardRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Users, Package, Edit2, Trash2, DollarSign, FileDown } from "lucide-react";
import { exportEventToPDF } from "@/lib/exportPDF";
import { TaskChecklist } from "@/components/TaskChecklist";
import { CrewStatusSection } from "./CrewStatusSection";
import { MiniCalendarStrip } from "./MiniCalendarStrip";
import { ExpandableNotes } from "./ExpandableNotes";
import { QBOverrideIndicator } from "./QBOverrideIndicator";

function normalizeGearAssignments(assignments) {
  if (!assignments) return [];
  return assignments.map(a => typeof a === 'string' ? { gear_id: a, quantity: 1 } : { gear_id: a.gear_id, quantity: a.quantity || 1 });
}

export const EventCard = forwardRef(function EventCard(
  { event, gear, crew, canEdit, canDelete, isOwner, isHighlighted, onEdit, onDelete, onRefresh },
  ref
) {
  const getGearName = (id) => gear.find(g => g.gear_id === id)?.name || id;
  const getCrewName = (id) => crew.find(c => c.crew_id === id)?.name || id;
  const gearAssignments = normalizeGearAssignments(event.gear_assignments);

  return (
    <Card
      ref={ref}
      data-testid={`event-card-${event.event_id}`}
      className={`border-border hover:border-primary/30 transition-colors animate-fade-in-up ${isHighlighted ? 'ring-2 ring-primary/50 border-primary/40' : ''}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-base truncate">{event.name}</h3>
            <p className="text-xs text-muted-foreground">{event.client_name}</p>
          </div>
          <Badge variant="outline" className={`shrink-0 text-xs status-${event.status}`}>{event.status}</Badge>
        </div>
        <QBOverrideIndicator event={event} />
        <div className="grid grid-cols-2 gap-y-2 text-xs text-muted-foreground mt-3">
          <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{event.venue || "TBD"}</div>
          <div className="flex items-center gap-1.5"><Package className="w-3 h-3" />{gearAssignments.length} gear items</div>
          <div className="flex items-center gap-1.5"><Users className="w-3 h-3" />{event.crew_assignments?.length || 0} crew</div>
          {isOwner && event.revenue > 0 && (
            <div className="flex items-center gap-1.5 text-primary"><DollarSign className="w-3 h-3" />${event.revenue.toLocaleString()}</div>
          )}
        </div>
        <MiniCalendarStrip startDate={event.start_date} endDate={event.end_date} />
        {gearAssignments.length > 0 && gear.length > 0 && (
          <div className="mt-3 pt-2 border-t border-border">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Assigned Gear</p>
            <div className="flex flex-wrap gap-1.5">
              {gearAssignments.map(a => (
                <Badge key={a.gear_id} variant="secondary" className="text-xs">{getGearName(a.gear_id)}{a.quantity > 1 && ` x${a.quantity}`}</Badge>
              ))}
            </div>
          </div>
        )}
        {canEdit && <CrewStatusSection event={event} crew={crew} canEdit={canEdit} />}
        {event.notes && <ExpandableNotes notes={event.notes} eventId={event.event_id} eventName={event.name} canEdit={canEdit} />}
        {!event.notes && <ExpandableNotes eventId={event.event_id} eventName={event.name} canEdit={canEdit} />}
        <TaskChecklist event={event} canEdit={canEdit} onRefresh={onRefresh} />
        <div className="flex gap-1 mt-3 pt-2 border-t border-border">
          <Button data-testid={`export-event-${event.event_id}`} variant="ghost" size="sm" className="h-7 text-xs" onClick={() => exportEventToPDF(event, gear, crew)}><FileDown className="w-3 h-3 mr-1" />Export</Button>
          {canEdit && <Button data-testid={`edit-event-${event.event_id}`} variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onEdit(event)}><Edit2 className="w-3 h-3 mr-1" />Edit</Button>}
          {canDelete && <Button data-testid={`delete-event-${event.event_id}`} variant="ghost" size="sm" className="h-7 text-xs text-red-400" onClick={() => onDelete(event.event_id)}><Trash2 className="w-3 h-3 mr-1" />Delete</Button>}
        </div>
      </CardContent>
    </Card>
  );
});
